import {
    Component,
    computed,
    input,
    InputSignal,
    linkedSignal,
    Signal,
    WritableSignal,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { TranslatePipe } from '@ngx-translate/core';

import {
    resolveSliderMode,
    SliderBlockSection,
    SliderItem,
    SliderMode,
} from '@core/cms/landing.cms';
import { CmsImage } from '@shared/ui/cms-image/cms-image';
import { TrustHtmlPipe } from '@shared/pipes/trust-html/trust-html.pipe';

/**
 * Renders a `ComponentSectionSliderBlock` using the home `app-clients` carousel: a heading with
 * prev/next arrows that page through one slide at a time (image + content). The CMS rich-text title
 * and slide content keep their plain `.cms-content` styling untouched.
 *
 * `sliderMode = tabsView` swaps the arrows for a tab strip — one tab per slide, labelled from the
 * item's `sliderTabName` — while the slide markup stays the same.
 */
@Component({
    selector: 'cms-slider-block',
    imports: [MatIconModule, TranslatePipe, CmsImage, TrustHtmlPipe],
    templateUrl: './slider-block.html',
    styleUrl: './slider-block.scss',
})
export class CmsSliderBlock {
    public readonly section: InputSignal<SliderBlockSection> = input.required<SliderBlockSection>();

    /** `true` when the CMS asks for the tabbed view: no arrows, one tab per slide instead. */
    protected readonly isTabsMode: Signal<boolean> = computed(
        (): boolean => resolveSliderMode(this.section()) === SliderMode.TabsView,
    );

    /** Index of the visible slide; falls back to the first slide when the section input changes. */
    protected readonly activeIndex: WritableSignal<number> = linkedSignal({
        source: this.section,
        computation: (): number => 0,
    });

    protected readonly current: Signal<SliderItem | undefined> = computed(
        (): SliderItem | undefined => this.section().sliderItems[this.activeIndex()],
    );

    protected selectSlide(index: number): void {
        this.activeIndex.set(index);
    }

    protected prev(): void {
        const count: number = this.section().sliderItems.length;

        if (count === 0) {
            return;
        }

        this.activeIndex.update((index: number): number => (index === 0 ? count - 1 : index - 1));
    }

    protected next(): void {
        const count: number = this.section().sliderItems.length;

        if (count === 0) {
            return;
        }

        this.activeIndex.update((index: number): number => (index + 1) % count);
    }
}
